import React, { useEffect, useMemo, useState } from 'react'
import { BlackParagraph } from 'components/atoms/Paragraph'
import Header from 'components/organisms/Table/atoms/Header'
import { useTranslation } from 'react-i18next'
import {
  Column,
  SortingRule,
  useExpanded,
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table'
import { arrayToCSV, emptyArray, sortByAlphabet, sortByDate } from 'utils/array'
import { highlight } from 'utils/highlight'
import Icon from 'components/atoms/Icon'
import { LinkTo } from 'components/atoms/Pointer'
import { IconButton } from 'components/molecules/Button'
import { Dropdown } from 'components/molecules/Dropdown'
import { MenuItem, Popup } from 'components/molecules/Popup'
import Table from 'components/organisms/Table'
import { MODALS } from 'components/organisms/Modal'
import { Contact } from '@types'
import { Page, PageHeader } from 'components/organisms/layout'
import { useUserContext } from 'contexts/UserContext'
import { AdvancedFilters, initialContactsGlobalFilter } from './components'
import dayjs from 'dayjs'
import { downloadCsv } from 'utils/download'
import { prepareContactsCsvData } from './utils'

import { ReactComponent as VisibleSvg } from 'brand/assets/common/Visibility_Visible.svg'
import { ReactComponent as NotVisibleSvg } from 'brand/assets/common/Visibility_Hidden.svg'
import DotAndText from '../../components/atoms/DotAndText'
import { useQuery } from 'react-query'
import { getContactsFn } from '../../api'
import { ContactsGlobalFilter } from './types'

const columns: Column<Contact>[] = [
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.visibility')} />
    },
    accessor: 'visible',
    width: 40,
    sortType: (a, b) => sortByDate(a.values.visible, b.values.visible),
    Cell: ({ value, row }) => {
      const Component = (row.original.i_userid === 0 ? row.original.auto_visible : value) ? VisibleSvg : NotVisibleSvg
      return <Component />
    },
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.lastName')} />
    },
    accessor: 'lastname',
    sortType: (a, b) => sortByAlphabet(a.values.lastname, b.values.lastname),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter?.search || '')} />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.firstName')} />
    },
    accessor: 'firstname',
    sortType: (a, b) => sortByAlphabet(a.values.firstname, b.values.firstname),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter?.search || '')} />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.info')} />
    },
    accessor: 'info',
    sortType: (a, b) => sortByAlphabet(a.values.info, b.values.info),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter?.search || '')} />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.email_work')} />
    },
    accessor: 'email_work',
    sortType: (a, b) => sortByAlphabet(a.values.email_work, b.values.email_work),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter?.search || '')} />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.identity')} />
    },
    accessor: 'identity',
    sortType: (a, b) => sortByAlphabet(a.values.identity, b.values.identity),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter?.search || '')} />
    ),
    width: 100,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.status')} />
    },
    accessor: 'id',
    sortType: (a, b) => sortByDate(a.values.last_login, b.values.last_login),
    Cell: ({ row }) => {
      const isActive = Boolean(row.original.i_userid)
      const [t] = useTranslation()

      return (
        <DotAndText
          text={isActive ? t('common.active') : t('common.notActive')}
          color={isActive ? '#499976' : '#d06d6e'}
        />
      )
    },
    width: 60,
  },
  {
    Header: () => null,
    accessor: 'firstname',
    id: 'edit',
    Cell: ({ cell }) => {
      const [t] = useTranslation()
      const { id } = cell.row.original

      return (
        <Dropdown
          button={
            <IconButton>
              <Icon icon="uil:ellipsis-v" />
            </IconButton>
          }
        >
          <Popup>
            <LinkTo to={`/contacts/${id}?modal=${MODALS.editContact}`}>
              <MenuItem value="edit" icon={<Icon icon="uil:edit" />} onClick={() => {}}>
                {t('common.edit')}
              </MenuItem>
            </LinkTo>
          </Popup>
        </Dropdown>
      )
    },
    width: 30,
    disableSortBy: true,
  },
]

export default function Contacts() {
  const [t] = useTranslation()
  const { state } = useUserContext()
  const { i_realm } = state?.realm

  const [pagination, setPagination] = useState(() => ({ pageIndex: 0, pageSize: 50 }))
  const [sortBy, setSortBy] = useState<SortingRule<Contact>[]>([])
  const [search, setSearch] = useState('')
  const [globalFilter, setGlobalFilter] = useState<ContactsGlobalFilter>(() => initialContactsGlobalFilter)

  const { data: contacts, isLoading } = useQuery(
    ['contacts', i_realm, pagination, sortBy[0], search, globalFilter],
    getContactsFn,
    {
      select: (data) => ({ items: data.items, total: data.total }),
      keepPreviousData: false,
    }
  )

  const pageCount = useMemo(
    () => Math.ceil(((contacts?.total as number) || 0) / pagination.pageSize) || 0,
    [contacts?.total, pagination.pageSize]
  )

  const tableProps = useTable(
    {
      columns,
      data: contacts?.items ?? emptyArray,
      initialState: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        globalFilter: { search },
        sortBy,
      }, // Pass our hoisted table state
      //globalFilter: globalFilterFunc,
      autoResetPage: false,
      disableSortRemove: true,
      manualPagination: true,
      manualSortBy: true,
      manualGlobalFilter: true,
      matablePropsnualGlobalFilter: true,
      pageCount: pageCount,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useFlexLayout,
    useResizeColumns
  )

  useEffect(() => {
    const { pageIndex, pageSize, sortBy: _sortBy } = tableProps.state
    if (pageIndex !== pagination.pageIndex || pageSize !== pagination.pageSize) {
      setPagination({ pageIndex, pageSize })
    }

    if (_sortBy[0]) {
      const item = _sortBy[0]
      const currentItem = sortBy[0]
      if (!currentItem || item.id !== currentItem?.id || item?.desc !== currentItem?.desc) {
        setSortBy([item])
      }
    }
  }, [tableProps.state, pagination, sortBy])

  const setSearchQuery = (query: string) => {
    tableProps.setGlobalFilter({
      ...tableProps.state.globalFilter,
      search: query,
    })
    setSearch(query)
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
  }

  const handleExportCsv = () => {
    const preparedData = prepareContactsCsvData(contacts?.items || [])
    const csvData = arrayToCSV(preparedData)
    downloadCsv(`devices[${dayjs(new Date()).format('DD-MM-HHmmss')}].csv`, csvData)
  }

  return (
    <Page
      title={t('page.contacts.title')}
      filter={
        <AdvancedFilters
          realmId={i_realm}
          globalFilter={globalFilter}
          setGlobalFilter={(filters) => {
            setGlobalFilter(filters)
          }}
        />
      }
    >
      <PageHeader
        title={t('page.contacts.title')}
        subtitle={contacts?.total || ''}
        search={search}
        onChangeSearch={(query) => {
          setSearchQuery(query)
        }}
        onExportCsv={handleExportCsv}
      />
      <Table
        isLoading={isLoading}
        {...tableProps}
        setGlobalFilter={() => {
          setSearchQuery('')
        }}
      />
    </Page>
  )
}
